import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { getYakurekiDetail } from '@/shared/apis/yakureki'
import {
  Yakureki,
  YakurekiDetail,
  isInProgressStatus,
} from '@/shared/types/yakurekiCore'
import { Soaps } from '@/shared/types/yakurekiSoap'

// UUIDフォーマットの検証
const isValidRequestId = (id: string): boolean => {
  if (id === 'new') return true
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(id)
}

interface UsePatientDetailQueryReturn {
  patientDetail?: YakurekiDetail
  soapList?: Omit<Soaps, 'id'>
  selectedPatient?: Yakureki
  isLoading: boolean
  error: Error | null
  reloadPatientDetail: () => Promise<void>
}

/**
 * 患者詳細取得クエリ
 *
 * 責務:
 * - 患者詳細データの取得（TanStack Query）
 * - SOAPデータの抽出
 * - 選択された患者の導出
 *
 * 使用例:
 * ```tsx
 * const { patientDetail, soapList, loading } = usePatientDetailQuery(requestId, patientList)
 * ```
 */
export const usePatientDetailQuery = (
  requestId?: string,
  patientList: Yakureki[] = []
): UsePatientDetailQueryReturn => {
  // 有効なリクエストIDかどうか
  const isValidId =
    requestId && requestId !== 'new' && isValidRequestId(requestId)

  const query = useQuery({
    queryKey: queryKeys.yakureki.detail(requestId ?? ''),
    queryFn: async (): Promise<YakurekiDetail> => {
      if (!requestId) {
        throw new Error('Request ID is required')
      }
      const { data } = await getYakurekiDetail(requestId)
      return data
    },
    enabled: !!isValidId,
    staleTime: 1000 * 60 * 5, // 5分キャッシュ
    // processing中は2秒ごとにポーリングして完了を検知
    // Gemini Batch post_batch (Pass 2 話者分離) 実行中は 15 秒ごとにポーリング
    refetchInterval: (query) => {
      const data = query.state.data
      if (data?.status && isInProgressStatus(data.status)) {
        return 2000
      }
      const postBatchStatus = data?.transcription?.post_batch_status
      if (postBatchStatus === 'pending' || postBatchStatus === 'running') {
        return 15000
      }
      return false
    },
  })

  // SOAPリストの抽出
  const soapList = useMemo(() => {
    const soaps = query.data?.soaps
    if (!soaps) return undefined
    const { id: _id, ...soapsWithoutId } = soaps
    return soapsWithoutId
  }, [query.data?.soaps])

  // 選択された患者の導出
  const selectedPatient = useMemo(() => {
    if (!requestId) return undefined

    if (!isValidRequestId(requestId)) {
      console.warn(`Invalid request ID format: ${requestId}`)
      return undefined
    }

    if (requestId === 'new') {
      return undefined
    }

    // URLのrequestIdを直接使用（サーバーサイドで権限チェック済み）
    return patientList.find((patient) => patient.id === requestId)
  }, [requestId, patientList])

  // 手動リロード関数
  const reloadPatientDetail = async () => {
    await query.refetch()
  }

  return {
    patientDetail: query.data,
    soapList,
    selectedPatient,
    isLoading: query.isLoading,
    error: query.error ?? null,
    reloadPatientDetail,
  }
}
